import { createContext, useContext, ReactNode } from "react";

interface ClientScopeValue {
  /** Account id of the client being managed by an operator. Undefined in self-service. */
  accountId?: string;
  /** Display name of the managed client — lets reused screens address the client by name (e.g. the booking-confirmation heading). Undefined in self-service. */
  clientName?: string;
  /** True when the managed client is removed — reused screens should suppress non-form edit affordances (DEV-1177). */
  isViewOnly?: boolean;
}

const ClientScopeContext = createContext<ClientScopeValue>({});

/**
 * Scopes the reused client Settings screens to a specific account. Wrap the
 * operator's Manage-Client Settings subtree in `<ClientScopeProvider accountId={clientId}>`.
 * The client portal renders the same screens with no provider, so `accountId`
 * stays undefined and self-service behaviour is unchanged.
 */
export const ClientScopeProvider = ({
  accountId,
  clientName,
  isViewOnly,
  children,
}: {
  accountId?: string;
  clientName?: string;
  isViewOnly?: boolean;
  children: ReactNode;
}) => <ClientScopeContext.Provider value={{ accountId, clientName, isViewOnly }}>{children}</ClientScopeContext.Provider>;

/**
 * Target account for the client Settings screens. Returns `{ accountId: undefined }`
 * in the client portal (self-service) and the managed client's id when an operator
 * is acting on a client record. Reused settings hooks pass this `accountId` to the
 * client-scoped queries/mutations (see `baseQueryWithClient`).
 */
export const useClientScope = (): ClientScopeValue => useContext(ClientScopeContext);
